import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone } from "lucide-react";
import { scrollToId } from "./Reveal";

const HERO_IMG =
    "https://images.unsplash.com/photo-1756483560049-e7b2208f99a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function Sparkles() {
    const ref = useRef(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const dots = Array.from({ length: 42 }, () => ({
            x: Math.random(),
            y: Math.random(),
            r: Math.random() * 1.6 + 0.4,
            s: Math.random() * 0.0006 + 0.0002,
            p: Math.random() * Math.PI * 2,
        }));
        const resize = () => {
            canvas.width = canvas.offsetWidth * dpr;
            canvas.height = canvas.offsetHeight * dpr;
        };
        resize();
        window.addEventListener("resize", resize);
        const draw = (t) => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            dots.forEach((d) => {
                d.y -= d.s;
                if (d.y < -0.05) d.y = 1.05;
                const tw = (Math.sin(t * 0.002 + d.p) + 1) / 2;
                ctx.beginPath();
                ctx.arc(d.x * canvas.width, d.y * canvas.height, d.r * dpr, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(230,198,135,${0.12 + tw * 0.5})`;
                ctx.fill();
            });
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);
    return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />;
}

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: 0,
        transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.16 },
    }),
};

export default function Hero({ start }) {
    const wrap = useRef(null);
    const { scrollYProgress } = useScroll({ target: wrap, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const state = start ? "show" : "hidden";

    return (
        <section id="hero" ref={wrap} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
            <motion.div style={{ y: imgY }} className="absolute inset-0 -top-24 -bottom-24">
                <img
                    src={HERO_IMG}
                    alt="Bridal gold jewellery at Maa Gayatri Jewellers"
                    className="w-full h-full object-cover object-top"
                />
            </motion.div>
            <div className="absolute inset-0 vignette-bottom" />
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian/85 via-obsidian/40 to-transparent" />
            <Sparkles />

            <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pb-20 sm:pb-28 pt-40">
                <div className="mask-line">
                    <motion.span variants={line} initial="hidden" animate={state} custom={0} className="font-hindi text-gold-light text-xl sm:text-2xl">
                        माँ गायत्री ज्वेलर्स
                    </motion.span>
                </div>

                <h1 className="font-display font-light leading-[1.05] mt-5 text-4xl sm:text-6xl lg:text-7xl max-w-3xl">
                    <span className="mask-line">
                        <motion.span variants={line} initial="hidden" animate={state} custom={1}>
                            Every ornament,
                        </motion.span>
                    </span>
                    <span className="mask-line">
                        <motion.span variants={line} initial="hidden" animate={state} custom={2} className="italic text-gold-gradient pr-2">
                            a legacy.
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={start ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 max-w-xl text-clay text-sm sm:text-base leading-relaxed"
                >
                    Gold, silver, diamond and high-end imitation jewellery — 22K & 18K gold, with trust and
                    purity. In the heart of Supaul, with Maa Gayatri's blessings.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={start ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-9 flex flex-col sm:flex-row gap-4"
                >
                    <a
                        href="tel:7209661797"
                        data-testid="hero-call-button"
                        className="relative inline-flex items-center justify-center gap-3 bg-gold text-obsidian font-semibold px-8 py-4 text-sm tracking-wide hover:bg-gold-light hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] transition-all duration-300"
                    >
                        <span className="ripple-dot" />
                        <Phone size={16} strokeWidth={2.4} />
                        Call Now — 7209661797
                    </a>
                    <button
                        onClick={() => scrollToId("collections")}
                        data-testid="hero-explore-button"
                        className="inline-flex items-center justify-center border border-gold/40 hover:border-gold-light text-gold-light px-8 py-4 text-sm tracking-wide transition-all duration-300"
                    >
                        Explore Collections
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
